import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  Lock,
  MessageSquare,
  Search,
  SendHorizonal,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderDash from "../../components/HeaderDash";
import { useAuthStore } from "../../hooks/useAuthStore";
import { useToast } from "../../hooks/useToast";
import { useQueryClient } from "@tanstack/react-query";
import {
  useConversationsQuery,
  useConversationMessagesQuery,
  useSendMessageMutation,
  useMarkConversationReadMutation,
} from "../../api/query/useMessageQueries";
import socketService from "../../api/socket";

const formatTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatRelativeTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const now = new Date();
  const diffMs = now - parsed;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "Yesterday";
  return parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

function Avatar({ src, name, size = "md" }) {
  const [failed, setFailed] = useState(false);
  const initials = String(name || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  const sizeClass =
    size === "lg"
      ? "h-14 w-14 text-lg"
      : size === "sm"
      ? "h-10 w-10 text-sm"
      : "h-12 w-12 text-base";

  if (!src || failed) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center rounded-full bg-[#F3E8EE] font-semibold text-[#7A054A] ${sizeClass}`}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={`shrink-0 rounded-full object-cover ${sizeClass}`}
      onError={() => setFailed(true)}
    />
  );
}

function ConversationItem({ conversation, active, onSelect }) {
  return (
    <button
      type="button"
      id={`conversation-${conversation.id}`}
      onClick={() => onSelect(conversation.id)}
      className={`flex w-full items-start gap-3 rounded-2xl px-4 py-4 text-left transition ${
        active ? "bg-[#F7F3F6]" : "hover:bg-[#F9FAFB]"
      }`}
    >
      <div className="relative">
        <Avatar src={conversation.avatar} name={conversation.name} size="sm" />
        {conversation.unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#570033] px-1 text-[10px] font-bold text-white">
            {conversation.unread}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold text-[#1D2939]">
            {conversation.name}
          </p>
          <span className="shrink-0 text-xs text-[#98A2B3]">
            {formatRelativeTime(conversation.updatedAt)}
          </span>
        </div>
        <p
          className={`mt-0.5 truncate text-sm ${
            conversation.unread > 0
              ? "font-semibold text-[#1D2939]"
              : "text-[#667085]"
          }`}
        >
          {conversation.preview}
        </p>
      </div>
    </button>
  );
}

function MessageBubble({ message }) {
  return (
    <div
      className={`flex flex-col ${message.mine ? "items-end" : "items-start"}`}
    >
      <div
        className={`max-w-[76%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          message.mine
            ? "rounded-br-md bg-[#570033] text-white"
            : "rounded-bl-md bg-[#F3F4F6] text-[#1D2939]"
        }`}
      >
        {message.text}
      </div>
      <span className="mt-1 px-1 text-xs text-[#98A2B3]">
        {formatTime(message.sentAt)}
      </span>
    </div>
  );
}

export default function UserMessages() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const toast = useToast();
  const currentUserId = user?.id || user?._id || "";
  const requestedConversationId = location.state?.conversationId || "";
  const requestedRecipientId =
    location.state?.recipientId || location.state?.assistantId || "";

  const [search, setSearch] = useState("");
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [draft, setDraft] = useState("");
  const [showMobileChat, setShowMobileChat] = useState(false);
  const messagesEndRef = useRef(null);
  const requestedConversationAppliedRef = useRef(false);

  // 1. Fetch conversations
  const { data: conversationsData, isLoading: conversationsLoading } =
    useConversationsQuery();

  const conversations = useMemo(() => {
    const raw = conversationsData?.data?.conversations || [];
    return raw.map((conv) => {
      const rec = conv.recipient || {};
      const lastMsg = conv.lastMessage;
      const unread =
        lastMsg && !lastMsg.isRead && lastMsg.sender !== currentUserId ? 1 : 0;
      return {
        id: conv._id,
        name: `${rec.firstName || ""} ${rec.lastName || ""}`.trim() || "User",
        avatar: rec.profilePicture || "",
        unread,
        preview: lastMsg?.text || "No messages yet",
        updatedAt: conv.updatedAt,
        recipientId: rec._id || rec.id || "",
        recipient: rec,
        // Closed once the trip ends; booking the assistant again reopens it.
        locked: Boolean(conv.isLocked),
      };
    });
  }, [conversationsData, currentUserId]);

  // 2. Load active conversation messages
  const { data: messagesData } = useConversationMessagesQuery(
    activeConversationId,
    {},
    { enabled: !!activeConversationId }
  );

  const messages = useMemo(() => {
    const raw = messagesData?.data?.items || [];
    return raw.map((msg) => ({
      id: msg._id,
      mine: (msg.sender?._id || msg.sender) === currentUserId,
      text: msg.text,
      sentAt: msg.createdAt,
    }));
  }, [messagesData, currentUserId]);

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.preview.toLowerCase().includes(q)
    );
  }, [conversations, search]);

  const activeConversation =
    conversations.find((c) => c.id === activeConversationId) ||
    filteredConversations[0] ||
    null;

  // Select the conversation requested by navigation state once, falling back to
  // the first visible conversation for normal messages-page visits.
  useEffect(() => {
    if (conversations.length === 0 && filteredConversations.length === 0) return;

    const hasRequestedTarget = Boolean(
      requestedConversationId || requestedRecipientId
    );

    if (
      hasRequestedTarget &&
      !requestedConversationAppliedRef.current &&
      conversationsLoading
    ) {
      return;
    }

    if (hasRequestedTarget && !requestedConversationAppliedRef.current) {
      const requestedConversation = requestedConversationId
        ? conversations.find(
            (conversation) => conversation.id === requestedConversationId
          )
        : null;

      const requestedRecipient = requestedRecipientId
        ? conversations.find(
            (conversation) =>
              String(conversation.recipientId) === String(requestedRecipientId)
          )
        : null;

      const requested = requestedConversation || requestedRecipient;
      if (requested) {
        requestedConversationAppliedRef.current = true;
        if (requested.id !== activeConversationId) {
          setActiveConversationId(requested.id);
          setShowMobileChat(true);
        }
        return;
      }
    }

    if (!activeConversationId && filteredConversations.length > 0) {
      setActiveConversationId(filteredConversations[0].id);
    }
  }, [
    activeConversationId,
    conversations,
    conversationsLoading,
    filteredConversations,
    requestedConversationId,
    requestedRecipientId,
  ]);

  // Scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Socket setup for real-time updates
  useEffect(() => {
    socketService.connect();

    const handleMessageReceived = (data) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      if (data?.conversationId) {
        queryClient.invalidateQueries({
          queryKey: ["messages", data.conversationId],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["messages"] });
      }
    };

    // A trip ending locks this thread, and the only signal this tab gets is the
    // notification push — without refetching, the composer stays visible until
    // the conversations query goes stale and every send bounces.
    const handleNotificationReceived = () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    };

    socketService.on("message", handleMessageReceived);
    socketService.on("notification", handleNotificationReceived);
    return () => {
      socketService.off("message", handleMessageReceived);
      socketService.off("notification", handleNotificationReceived);
    };
  }, [queryClient]);

  const sendMessageMutation = useSendMessageMutation();
  const markReadMutation = useMarkConversationReadMutation();

  const handleSelectConversation = (conversationId) => {
    setActiveConversationId(conversationId);
    setShowMobileChat(true);
    markReadMutation.mutate(conversationId);
  };

  const handleSendMessage = (event) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value || !activeConversation || activeConversation.locked) return;

    sendMessageMutation.mutate(
      {
        recipientId: activeConversation.recipient?._id,
        text: value,
      },
      {
        onSuccess: () => {
          setDraft("");
        },
        onError: (err) => {
          toast.apiError(err, "Failed to send message.");
        },
      }
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      handleSendMessage(event);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f7f9]">
      <HeaderDash />

      <main className="mx-auto w-full max-w-[1200px] px-4 py-8 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#570033]">Messages</h1>
          <p className="text-sm text-slate-500 mt-1">
            Your conversations with Pulo Assistants
          </p>
        </div>

        <div className="grid h-[calc(100vh-14rem)] min-h-[500px] gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
          {/* Conversation list */}
          <section
            className={`min-h-0 flex-col rounded-2xl border border-gray-200 bg-white shadow-sm ${
              showMobileChat ? "hidden lg:flex" : "flex"
            }`}
          >
            <div className="border-b border-[#EEF0F3] px-4 py-4">
              <label className="flex items-center gap-3 rounded-full border border-[#D0D5DD] bg-gray-50 px-4 py-2.5">
                <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search conversations"
                  className="w-full border-0 bg-transparent text-sm text-[#1D2939] outline-none placeholder:text-[#98A2B3]"
                />
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
              {conversationsLoading ? (
                <div className="space-y-3 px-2 pt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex animate-pulse items-center gap-3 py-2"
                    >
                      <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-1/2 rounded bg-gray-200" />
                        <div className="h-3 w-3/4 rounded bg-gray-200" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F3E8EE]">
                    <MessageSquare className="h-6 w-6 text-[#570033]" />
                  </div>
                  <p className="text-sm font-medium text-[#667085]">
                    {search ? "No results found" : "No conversations yet"}
                  </p>
                  {!search && (
                    <p className="text-xs text-[#98A2B3]">
                      Message an assistant from their profile to start a
                      conversation
                    </p>
                  )}
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    active={conversation.id === activeConversation?.id}
                    onSelect={handleSelectConversation}
                  />
                ))
              )}
            </div>
          </section>

          {/* Chat panel */}
          <section
            className={`min-h-0 flex-col rounded-2xl border border-gray-200 bg-white shadow-sm ${
              showMobileChat ? "flex" : "hidden lg:flex"
            }`}
          >
            {activeConversation ? (
              <>
                {/* Chat header */}
                <div className="flex items-center justify-between border-b border-[#EEF0F3] px-5 py-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowMobileChat(false)}
                      className="rounded-full p-1.5 text-[#667085] transition hover:bg-[#F2F4F7] lg:hidden"
                      aria-label="Back to conversations"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <Avatar
                      src={activeConversation.avatar}
                      name={activeConversation.name}
                      size="sm"
                    />
                    <div>
                      <p className="text-base font-semibold text-[#1D2939]">
                        {activeConversation.name}
                      </p>
                      <p className="text-xs text-[#667085]">Pulo Assistant</p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
                  <div className="flex min-h-full flex-col justify-end gap-4">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                        <p className="text-sm text-[#667085]">
                          Start the conversation below
                        </p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <MessageBubble key={msg.id} message={msg} />
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Input */}
                {activeConversation.locked ? (
                  <div className="border-t border-[#EEF0F3] px-5 py-5">
                    <div className="flex flex-col items-center gap-3 rounded-2xl bg-[#F9FAFB] px-5 py-5 text-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F3E8EE]">
                        <Lock className="h-4 w-4 text-[#570033]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1D2939]">
                          This conversation is closed
                        </p>
                        <p className="mt-1 text-sm text-[#667085]">
                          Your trip with {activeConversation.name} has ended. Book
                          them again to pick the conversation back up.
                        </p>
                      </div>
                      {activeConversation.recipientId ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/assistant/${activeConversation.recipientId}`)
                          }
                          className="rounded-xl bg-[#570033] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#450029]"
                        >
                          Book again
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                <div className="border-t border-[#EEF0F3] px-5 py-4">
                  <form
                    onSubmit={handleSendMessage}
                    className="flex items-center gap-3 rounded-full border border-[#D0D5DD] bg-white px-4 py-2.5"
                  >
                    <input
                      type="text"
                      id="message-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your message..."
                      className="w-full border-0 bg-transparent text-sm text-[#1D2939] outline-none placeholder:text-[#98A2B3]"
                    />

                    <button
                      type="submit"
                      disabled={!draft.trim() || sendMessageMutation.isPending}
                      id="send-message-btn"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#570033] text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Send message to ${activeConversation.name}`}
                    >
                      <SendHorizonal className="h-4 w-4" />
                    </button>
                  </form>
                </div>
                )}
              </>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F3E8EE]">
                  <MessageSquare className="h-7 w-7 text-[#570033]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1D2939]">
                    Your messages live here
                  </p>
                  <p className="mt-1 text-sm text-[#667085]">
                    Select a conversation or message an assistant to get started
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => (window.location.href = "/travel-assistant")}
                  className="rounded-xl bg-[#570033] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#450029]"
                >
                  Find an Assistant
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
